import React, { useState, useContext, useEffect, useCallback } from 'react';
import { Header, DataContainer, FlexDiv } from './styles';
import { DocListOverlay, CloseIcon, DocListFooter } from './overlayScreenStyles';
import { Icon, Button, message, Checkbox } from 'antd';
import FiltersContainer from './FilterSection';
import DocumentList from './DocumentList';
import ConfirmationModal from '../../components/ConfirmationModal/ConfirmationModal';
import { MailRoomContext } from '../../context/mailRoomContext';
import { deleteDocuments } from '../../utils/services/mailRoomAPI';
import { getWeek } from '../../utils/javascript';
import { withTranslation } from 'react-i18next';
import { DELETE_DOCUMENT_MODAL } from './translationValues';

const NAMESPACES = ['DELETE_DOCUMENT_MODAL'];

const DeleteDocuments = ({ setDeleteDocuments, setDefaultWeek, t }) => {
  const [selectedDocIds, setSelectedDocIds] = useState([]);
  const [excludedDocIds, setExcludedDocIds] = useState([]);
  const [selectedAll, setSelectedAll] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const LIMIT = window.innerHeight > 1000 ? 30 : window.innerHeight > 800 ? 20 : 16;

  const {
    searchByDocName,
    payloadTags,
    startAndEndDate,
    setStartAndEndDate,
    pageFilter,
    docsCount,
    clearAllFilters,
  } = useContext(MailRoomContext);

  const [confirmationModal, setConfirmationModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleSelectAllChange = value => {
    setSelectedDocIds([]);
    setExcludedDocIds([]);
    setSelectedAll(value);
  };

  const escFunction = useCallback(
    event => {
      if (event.key === 'Escape') {
        clearAllFilters();
        setDeleteDocuments(false);
        setDefaultWeek();
      }
    },
    [setDeleteDocuments, setDefaultWeek, clearAllFilters],
  );

  useEffect(() => {
    document.addEventListener('keydown', escFunction, false);
    return () => {
      document.removeEventListener('keydown', escFunction, false);
    };
  }, [escFunction]);

  useEffect(() => {
    let startAndEndDate = getWeek();
    let dates = {
      startDate: startAndEndDate.startDate,
      endDate: startAndEndDate.endDate,
    };
    setStartAndEndDate(dates);
  }, []);

  const onDelete = async () => {
    setIsDeleting(true);
    let payload = {};
    if (selectedAll) {
      payload.dateFrom = startAndEndDate.startDate;
      payload.dateTo = startAndEndDate.endDate;
      if (searchByDocName) {
        payload.nameSearched = searchByDocName;
      }
      if (excludedDocIds.length > 0) {
        payload.excludedDocumentIds = excludedDocIds;
      }
      if(pageFilter) {
        payload.documentPages = parseInt(pageFilter);
      }
      payload = { ...payload, ...payloadTags };
    } else {
      if (selectedDocIds.length > 0) {
        payload.mailRoomDocumentIds = selectedDocIds;
      }
    }
    const response = await deleteDocuments(payload);
    if (response) {
      if (response.errors && response.errors.length === 0) {
        message.success(
          t(
            DELETE_DOCUMENT_MODAL.DOCUMENT_DELETED_SUCCESS_MESSAGE.key,
            DELETE_DOCUMENT_MODAL.DOCUMENT_DELETED_SUCCESS_MESSAGE.value,
          ),
        );
        onCancel();
        let dates = {
          startDate: startAndEndDate.startDate,
          endDate: startAndEndDate.endDate,
        };
        setStartAndEndDate(dates);
      }
    }
    setIsDeleting(false);
    onCancel();
  };

  const onCancel = () => {
    setConfirmationModal(false);
  };

  const docsToBeDeleted = selectedAll ? docsCount - excludedDocIds.length : selectedDocIds.length;
  return (
    <DocListOverlay>
      <Header isLoading={isLoading} padding="1.5rem" height="20vh" justifyContent={'space-between'}>
        <h3 className="overlayScreenTitle">
          {t(
            DELETE_DOCUMENT_MODAL.DELETE_DOCUMENT_TITLE.key,
            DELETE_DOCUMENT_MODAL.DELETE_DOCUMENT_TITLE.value,
          )}
        </h3>
        <FlexDiv isLoading={isLoading}>
          <FiltersContainer />
          <CloseIcon>
            <Icon
              type="close"
              onClick={() => {
                clearAllFilters();
                setDeleteDocuments(false);
                setDefaultWeek();
              }}
            />
          </CloseIcon>
        </FlexDiv>
      </Header>
      <DataContainer height={'70vh'} width={'100%'}>
        <DocumentList
          isCentered
          pageLimit={LIMIT}
          isLoading={isLoading}
          setIsLoading={setIsLoading}
          selectable={true}
          selectedDocIds={selectedDocIds}
          setSelectedDocIds={setSelectedDocIds}
          excludedDocIds={excludedDocIds}
          setExcludedDocIds={setExcludedDocIds}
          selectedAll={selectedAll}
          setSelectedAll={setSelectedAll}
        />
      </DataContainer>
      <DocListFooter justifyContent={'flex-end'} isLoading={isLoading} height={'10vh'}>
        {docsCount > 0 && (
          <Checkbox
            onChange={e => handleSelectAllChange(e.target.checked)}
            checked={selectedAll}
            indeterminate={excludedDocIds.length > 0}
          >
            {t(
              DELETE_DOCUMENT_MODAL.SELECT_ALL_DOCUMENTS_CHECKBOX_LABEL.key,
              DELETE_DOCUMENT_MODAL.SELECT_ALL_DOCUMENTS_CHECKBOX_LABEL.value,
            )}
          </Checkbox>
        )}
        <Button
          className="customButton deleteButton"
          onClick={() =>
            docsToBeDeleted >= 1
              ? setConfirmationModal(true)
              : message.error(
                  t(
                    DELETE_DOCUMENT_MODAL.SELECT_A_DOCUMENT_WARNING_MESSAGE.key,
                    DELETE_DOCUMENT_MODAL.SELECT_A_DOCUMENT_WARNING_MESSAGE.value,
                  ),
                )
          }
        >
          {docsToBeDeleted > 0
            ? `${t(
                DELETE_DOCUMENT_MODAL.DELETE_DOCUMENT_BUTTON.key,
                DELETE_DOCUMENT_MODAL.DELETE_DOCUMENT_BUTTON.value,
                { Count: docsToBeDeleted },
              )} ${docsToBeDeleted > 1 ? 's' : ''}`
            : t(DELETE_DOCUMENT_MODAL.DELETE_BUTTON.key, DELETE_DOCUMENT_MODAL.DELETE_BUTTON.value)}
        </Button>
      </DocListFooter>
      <ConfirmationModal
        visibility={confirmationModal}
        onCancel={onCancel}
        onConfirm={() => onDelete()}
        confirmationText={`${t(
          DELETE_DOCUMENT_MODAL.DELETE_CONFIRMATION_MESSAGE.key,
          DELETE_DOCUMENT_MODAL.DELETE_CONFIRMATION_MESSAGE.value,
          { Count: docsToBeDeleted },
        )}  ${docsToBeDeleted > 1 ? 's' : ''}?`}
        confirmButtonText={t(
          DELETE_DOCUMENT_MODAL.DELETE_BUTTON.key,
          DELETE_DOCUMENT_MODAL.DELETE_BUTTON.value,
        )}
        isLoading={isDeleting}
      />
    </DocListOverlay>
  );
};
export default withTranslation(NAMESPACES)(DeleteDocuments);
